import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";

const HeroSection = () => {
  const scrollToContact = () => {
    document.getElementById("contact-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToAI = () => {
    document.getElementById("ai-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="pt-12 pb-16 sm:pt-16 sm:pb-20 md:pt-20 md:pb-28 lg:pt-24 lg:pb-32 relative overflow-hidden">
      {/* Animated background - reduced on mobile for performance */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-48 h-48 sm:w-72 sm:h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-primary/5 rounded-full blur-3xl animate-pulse delay-1000 hidden sm:block" />
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6">
          {/* Trust badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <span className="text-xs sm:text-sm font-medium text-primary">Trusted by 500+ Events Nationwide</span>
          </div>

          {/* Main headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight text-foreground animate-fade-in [animation-delay:100ms]">
            Live Music That <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            <span className="bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
              Makes Events Legendary
            </span>
          </h1>
          
          {/* Subheadline */}
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-2 animate-fade-in [animation-delay:200ms]">
            Skip the endless emails and awkward calls. Our AI matches you with vetted musicians in minutes—
            <span className="text-foreground font-medium"> saving you 40% on booking costs.</span>
          </p>

          {/* Primary + Secondary CTA */}
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 justify-center items-center pt-4 sm:pt-6 animate-fade-in [animation-delay:300ms] px-4 sm:px-0">
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 active:scale-95 sm:hover:scale-105 group" 
              onClick={scrollToContact}
            >
              Get Your Free Quote
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 active:scale-95 sm:hover:scale-105 transition-all duration-300 group" 
              onClick={scrollToAI}
            >
              <Play className="mr-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:scale-110 transition-transform" />
              See How It Works
            </Button>
          </div>

          {/* Micro-conversion */}
          <p className="text-xs sm:text-sm text-muted-foreground animate-fade-in [animation-delay:350ms] flex flex-wrap justify-center gap-x-2 gap-y-1">
            <span>✓ Free quote in 24hrs</span>
            <span className="hidden sm:inline">•</span>
            <span>✓ Multi-day event packages</span>
            <span className="hidden sm:inline">•</span>
            <span>✓ Zero obligation</span>
          </p>

          {/* Social proof */}
          <div className="pt-6 sm:pt-8 animate-fade-in [animation-delay:400ms]">
            <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">Trusted by leading companies and couples</p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-6 opacity-60 text-sm sm:text-lg font-semibold">
              <span>Fortune 500</span>
              <span>•</span>
              <span>Celebrity Weddings</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">Tech Launches</span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 pt-8 sm:pt-10 animate-fade-in [animation-delay:500ms]">
            <div className="space-y-0.5 sm:space-y-1 p-3 sm:p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 transition-colors duration-300">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary">130+</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Elite Musicians</div>
            </div>
            <div className="space-y-0.5 sm:space-y-1 p-3 sm:p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 transition-colors duration-300">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary">15+</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Instruments</div>
            </div>
            <div className="space-y-0.5 sm:space-y-1 p-3 sm:p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 transition-colors duration-300">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary">98%</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Client Satisfaction</div>
            </div>
            <div className="space-y-0.5 sm:space-y-1 p-3 sm:p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 transition-colors duration-300">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary">24hr</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Quote Response</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
